// ZETA特集コレクションセクションUI（2-1/2-2）: 各空コンテナをwindow.Zeta経由で取得し共通カードで描画。IntersectionObserverで接近時に遅延ロード＋staggerフェードイン（非対応時は即時読込）。

(function () {
  'use strict';

  const SELECTOR = '[data-c_zeta-collection]';
  const SOURCE_TYPE_ZETA = 'zeta';
  const SOURCE_TYPE_SHOPIFY = 'shopify';

  // 2-2: 遅延ロードのrootMargin（手前120pxで取得し複数セクションの同時発火を抑制）
  const LAZY_ROOT_MARGIN = '120px 0px';
  // 2-2: stagger表示の1枚あたり遅延と上限枚数
  const STAGGER_STEP_MS = 60;
  const STAGGER_MAX = 8;
  const PREFERS_REDUCED_MOTION =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // セクションの設定JSON（Liquid出力のapplication/json）を取り出す
  const parseConfig = (root) => {
    const node = root.querySelector('[data-c_zeta-collection-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_) {
      return {};
    }
  };

  // 設定 → Zeta API のリクエストパラメータへ変換
  const buildParams = (cfg, collectionHandle) => {
    const params = {
      q: cfg.q || '',
      l: cfg.count || 10,
      o: 0,
      s: cfg.sort || '-score'
    };
    if (collectionHandle) params.collection = collectionHandle;
    const filters = cfg.filters || {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) params[key] = value;
    });
    return params;
  };

  // 取得結果のカードを描画する
  const renderItems = (root, items, cfg) => {
    const grid = root.querySelector('[data-c_zeta-collection-grid]');
    const status = root.querySelector('[data-c_zeta-collection-status]');
    const template = root.querySelector('[data-c_zeta-card-template]');
    if (!grid || !template) return;

    while (grid.firstChild) grid.removeChild(grid.firstChild);

    if (!items.length) {
      if (status) {
        status.hidden = false;
        status.textContent = '商品が見つかりませんでした。';
      }
      return;
    }

    const frag = document.createDocumentFragment();
    const cards = [];
    items.forEach((item, i) => {
      const card = window.Zeta.buildProductCard(item, template, { showSwatch: false, showDiscount: false });
      if (!card) return;
      const rank = card.querySelector('[data-card-rank]');
      if (cfg && cfg.show_rank && rank) {
        rank.textContent = i + 1;
        rank.hidden = false;
      }
      if (!PREFERS_REDUCED_MOTION) {
        // stagger初期状態（.c_zeta-card--revealでopacity:0）＋順番の遅延
        card.classList.add('c_zeta-card--reveal');
        card.style.transitionDelay = Math.min(i, STAGGER_MAX) * STAGGER_STEP_MS + 'ms';
      }
      cards.push(card);
      frag.appendChild(card);
    });
    grid.appendChild(frag);
    if (status) status.hidden = true;

    setupCarouselNav(root, grid);

    // 次フレームで reveal クラスを付与し、順次フェードインさせる
    if (!PREFERS_REDUCED_MOTION && cards.length) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          cards.forEach((card) => card.classList.add('is-in'));
        });
      });
    }
  };

  // カルーセルの左右矢印ナビ構成（オーバーフロー時のみ表示・1ページ分スクロール・端でdisabled・画像中央に縦位置）
  const setupCarouselNav = (root, grid) => {
    const viewport = root.querySelector('[data-c_zeta-collection-viewport]');
    const prev = root.querySelector('[data-c_zeta-collection-prev]');
    const next = root.querySelector('[data-c_zeta-collection-next]');
    if (!viewport || !prev || !next) return;

    // 矢印の縦位置を商品画像の中央に合わせる
    const positionArrows = () => {
      const activeGrid = getActiveGrid(root);
      if (!activeGrid) return;
      const image = activeGrid.querySelector('.product-item__image-wrapper, .product-item__aspect-ratio');
      if (image) {
        viewport.style.setProperty('--c_zeta-arrow-top', image.offsetHeight / 2 + 'px');
      }
    };

    // スクロール位置で表示/非表示を更新（本番product-list__arrow同様、端はdisabledでホバー表示対象外）
    const updateState = () => {
      const activeGrid = getActiveGrid(root);
      if (!activeGrid) return;
      const isCarousel = activeGrid.getAttribute('data-layout') === 'carousel';
      const maxScroll = activeGrid.scrollWidth - activeGrid.clientWidth;
      const hasOverflow = isCarousel && maxScroll > 1;
      if (!hasOverflow) {
        prev.hidden = true;
        next.hidden = true;
        prev.disabled = true;
        next.disabled = true;
        return;
      }
      prev.hidden = false;
      next.hidden = false;
      prev.disabled = activeGrid.scrollLeft <= 1;
      next.disabled = activeGrid.scrollLeft >= maxScroll - 1;
    };

    const scrollByPage = (direction) => {
      const activeGrid = getActiveGrid(root);
      if (!activeGrid) return;
      activeGrid.scrollBy({ left: direction * activeGrid.clientWidth, behavior: 'smooth' });
    };

    if (!root.dataset.cZetaCarouselBound) {
      root.dataset.cZetaCarouselBound = '1';
      prev.addEventListener('click', () => scrollByPage(-1));
      next.addEventListener('click', () => scrollByPage(1));
      window.addEventListener('resize', () => {
        positionArrows();
        updateState();
      });
    }
    if (!grid.dataset.cZetaScrollBound) {
      grid.dataset.cZetaScrollBound = '1';
      grid.addEventListener('scroll', updateState, { passive: true });
    }

    positionArrows();
    updateState();
    // 画像読込で高さが確定した後に縦位置を再計算
    const firstImg = grid.querySelector('img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', positionArrows, { once: true });
    }
  };

  const getActiveTab = (root) =>
    root.querySelector('[data-c_zeta-collection-tab][aria-expanded="true"]');

  const getActiveGrid = (root) => {
    const activeTab = getActiveTab(root);
    if (activeTab && activeTab.getAttribute('data-source-type') === SOURCE_TYPE_SHOPIFY) {
      const blockId = activeTab.getAttribute('data-block-id') || '';
      const shopifyGrid = root.querySelector('[data-c_zeta-collection-shopify-grid="' + blockId + '"]');
      if (shopifyGrid) return shopifyGrid;
    }
    return root.querySelector('[data-c_zeta-collection-grid]');
  };

  // Shopifyカードにも画像比・寄せ・タイポを合わせるためZETAカードと同じクラスを付ける（c_product-itemのclassはLiquid側で追加できない）
  const normalizeShopifyCards = (root) => {
    root.querySelectorAll('[data-c_zeta-collection-shopify-grid] > .product-item').forEach((card) => {
      card.classList.add('c_zeta-card');
    });
  };

  const showActiveGrid = (root) => {
    const zetaGrid = root.querySelector('[data-c_zeta-collection-grid]');
    const shopifyGrids = root.querySelectorAll('[data-c_zeta-collection-shopify-grid]');
    if (zetaGrid) zetaGrid.hidden = true;
    shopifyGrids.forEach((shopifyGrid) => {
      shopifyGrid.hidden = true;
    });
    const activeGrid = getActiveGrid(root);
    if (activeGrid) activeGrid.hidden = false;
    return activeGrid;
  };

  // タブのアクティブ状態から取得対象のShopifyコレクションhandleを返す
  const getActiveCollectionHandle = (root) => {
    const active = getActiveTab(root);
    if (active && active.hidden) return '';
    return active ? active.getAttribute('data-collection-handle') || '' : '';
  };

  // タブのアクティブ状態をaria-expandedとis-activeで同期する
  const setActiveTab = (tabs, activeTab) => {
    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  };

  const updateMoreLink = (root, activeTab) => {
    const moreLink = root.querySelector('[data-c_zeta-collection-more]');
    const moreUrl = activeTab && activeTab.getAttribute('data-more-url');
    if (!moreLink || !moreUrl) return;
    moreLink.href = moreUrl;
    const moreText = activeTab.getAttribute('data-more-text');
    if (moreText) moreLink.textContent = moreText;
  };

  // 第1階層を切り替え、対象グループの先頭サブタブで再取得する
  const switchGroup = (root, activeGroup) => {
    const groups = Array.from(root.querySelectorAll('[data-c_zeta-collection-group]'));
    const panels = Array.from(root.querySelectorAll('[data-c_zeta-collection-group-panel]'));
    const tabs = Array.from(root.querySelectorAll('[data-c_zeta-collection-tab]'));
    const groupId = activeGroup.getAttribute('data-group-id') || '';
    const activePanel = panels.find(
      (panel) => panel.getAttribute('data-c_zeta-collection-group-panel') === groupId
    );

    setActiveTab(groups, activeGroup);
    panels.forEach((panel) => {
      panel.hidden = true;
    });
    if (activePanel && activePanel.dataset.single !== 'true') activePanel.hidden = false;

    const activeTab = activePanel
      ? Array.from(activePanel.querySelectorAll('[data-c_zeta-collection-tab]')).find((tab) => !tab.hidden)
      : null;
    setActiveTab(tabs, activeTab);
    updateMoreLink(root, activeTab);
    if (lazyObserver) lazyObserver.unobserve(root);
    loadSection(root);
  };

  // 初回だけ各タブのcollectionを軽量取得し、0件タブを非表示にする
  const ensureTabsWithResults = async (root, cfg) => {
    const tabs = Array.from(root.querySelectorAll('[data-c_zeta-collection-tab]'));
    if (!tabs.length || root.dataset.cZetaTabsProbed) return;
    root.dataset.cZetaTabsProbed = '1';

    await Promise.all(
      tabs.map(async (tab) => {
        if (tab.getAttribute('data-source-type') === SOURCE_TYPE_SHOPIFY) {
          if (Number(tab.getAttribute('data-product-count')) === 0) tab.hidden = true;
          return;
        }
        const handle = tab.getAttribute('data-collection-handle') || '';
        if (!handle) return;
        try {
          const data = await window.Zeta.fetchItems({
            ...buildParams(cfg, handle),
            l: 1,
            o: 0,
            nl: 1
          });
          const total = Number(data && data.result && data.result.attribute && data.result.attribute.total);
          if (total === 0) tab.hidden = true;
        } catch (err) {
          console.warn('[c_zeta-collection] tab probe failed', err);
        }
      })
    );

    const visibleTabs = tabs.filter((tab) => !tab.hidden);
    const groups = Array.from(root.querySelectorAll('[data-c_zeta-collection-group]'));
    if (groups.length) {
      const panels = Array.from(root.querySelectorAll('[data-c_zeta-collection-group-panel]'));
      groups.forEach((group) => {
        const groupId = group.getAttribute('data-group-id') || '';
        const panel = panels.find(
          (candidate) => candidate.getAttribute('data-c_zeta-collection-group-panel') === groupId
        );
        const hasVisibleTab =
          panel && Array.from(panel.querySelectorAll('[data-c_zeta-collection-tab]')).some((tab) => !tab.hidden);
        group.hidden = !hasVisibleTab;
      });

      const rankingWrapper = root.querySelector('.c_ranking-container');
      const groupWrapper = root.querySelector('.c_tabs-nav');
      const visibleGroups = groups.filter((group) => !group.hidden);
      if (!visibleTabs.length) {
        if (rankingWrapper) rankingWrapper.hidden = true;
        if (groupWrapper) groupWrapper.hidden = true;
        setActiveTab(tabs, null);
        setActiveTab(groups, null);
        return;
      }

      if (groupWrapper) groupWrapper.hidden = visibleGroups.length <= 1;
      const activeGroup = groups.find((group) => group.getAttribute('aria-expanded') === 'true');
      if (!activeGroup || activeGroup.hidden) {
        switchGroup(root, visibleGroups[0]);
        return false;
      }

      const activePanel = panels.find(
        (panel) =>
          panel.getAttribute('data-c_zeta-collection-group-panel') ===
          activeGroup.getAttribute('data-group-id')
      );
      const activeTab = tabs.find((tab) => tab.getAttribute('aria-expanded') === 'true');
      if (!activeTab || activeTab.hidden) {
        const firstVisibleTab = activePanel
          ? Array.from(activePanel.querySelectorAll('[data-c_zeta-collection-tab]')).find((tab) => !tab.hidden)
          : null;
        setActiveTab(tabs, firstVisibleTab);
        updateMoreLink(root, firstVisibleTab);
      }
      return;
    }

    // タブのラッパーはセクションによって異なる（下線版=c_ranking-container / ピル版=c_tabs-nav）
    const tabsWrapper = tabs.length > 1 ? root.querySelector('.c_ranking-container, .c_tabs-nav') : null;
    if (!visibleTabs.length) {
      if (tabsWrapper) tabsWrapper.hidden = true;
      setActiveTab(tabs, null);
      return;
    }

    if (tabsWrapper) tabsWrapper.hidden = false;
    const active = tabs.find((tab) => tab.getAttribute('aria-expanded') === 'true');
    if (!active || active.hidden) setActiveTab(tabs, visibleTabs[0]);
    updateMoreLink(root, visibleTabs.find((tab) => tab.getAttribute('aria-expanded') === 'true'));
  };

  // 再取得前に表示を初期状態へ戻す
  const resetCollectionView = (root) => {
    const grid = root.querySelector('[data-c_zeta-collection-grid]');
    const status = root.querySelector('[data-c_zeta-collection-status]');
    const prev = root.querySelector('[data-c_zeta-collection-prev]');
    const next = root.querySelector('[data-c_zeta-collection-next]');
    if (grid) {
      grid.scrollLeft = 0;
      while (grid.firstChild) grid.removeChild(grid.firstChild);
    }
    if (status) {
      status.hidden = false;
      status.textContent = '読み込み中…';
    }
    if (prev) prev.disabled = true;
    if (next) next.disabled = true;
  };

  // 閲覧履歴（2-7）: localStorageのZETA形状スナップショットを新しい順で読む（ZETAにID一括取得が無いため取得不要で表示）
  const VIEWED_KEY = 'c_zeta_viewed';
  const readViewed = (limit) => {
    let list = [];
    try {
      const raw = localStorage.getItem(VIEWED_KEY);
      list = raw ? JSON.parse(raw) : [];
    } catch (_) {
      list = [];
    }
    if (!Array.isArray(list)) return [];
    return list.filter((it) => it && it.id).slice(0, limit || 10);
  };

  // 閲覧履歴が空のときセクションごと非表示（BoostAI同様）
  const hideSection = (root) => {
    const wrapper = root.closest('.shopify-section');
    if (wrapper) wrapper.hidden = true;
    else root.hidden = true;
  };

  // 1セクション分を取得・描画する（source=viewedはlocalStorage、それ以外はZETA取得）
  const loadSection = async (root) => {
    if (!window.Zeta) return;
    const status = root.querySelector('[data-c_zeta-collection-status]');
    const cfg = parseConfig(root);

    // 閲覧履歴はfetchせずlocalStorageから描画（アンダースコア属性はgetAttributeで参照）
    if (root.getAttribute('data-c_zeta-collection-source') === 'viewed') {
      const items = readViewed(cfg.count || 10);
      if (!items.length) {
        hideSection(root);
        return;
      }
      renderItems(root, items, cfg);
      return;
    }

    try {
      const shouldContinue = await ensureTabsWithResults(root, cfg);
      if (shouldContinue === false) return;
      const activeTab = getActiveTab(root);
      const sourceType = activeTab ? activeTab.getAttribute('data-source-type') : SOURCE_TYPE_ZETA;
      const activeGrid = showActiveGrid(root);
      if (sourceType === SOURCE_TYPE_SHOPIFY) {
        if (status) status.hidden = true;
        if (activeGrid) setupCarouselNav(root, activeGrid);
        return;
      }
      resetCollectionView(root);
      const data = await window.Zeta.fetchItems(buildParams(cfg, getActiveCollectionHandle(root)));
      const items = (data && data.result && data.result.items) || [];
      renderItems(root, items, cfg);
    } catch (err) {
      console.error('[c_zeta-collection]', err);
      if (status) {
        status.hidden = false;
        // 実APIはCORS開通前、ブラウザ確認は ?zeta_dev=1（localモード）が必要
        status.textContent = '商品の取得に失敗しました。';
      }
    }
  };

  // タブ切替時にcollection条件を差し替えて再取得する
  const setupTabs = (root) => {
    const tabs = Array.from(root.querySelectorAll('[data-c_zeta-collection-tab]'));
    if (!tabs.length || root.dataset.cZetaTabsBound) return;
    root.dataset.cZetaTabsBound = '1';
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (tab.getAttribute('aria-expanded') === 'true') return;
        setActiveTab(tabs, tab);
        updateMoreLink(root, tab);
        if (lazyObserver) lazyObserver.unobserve(root);
        loadSection(root);
      });
    });
  };

  // 第1階層タブのクリックを束ねる
  const setupGroups = (root) => {
    const groups = Array.from(root.querySelectorAll('[data-c_zeta-collection-group]'));
    if (!groups.length || root.dataset.cZetaGroupsBound) return;
    root.dataset.cZetaGroupsBound = '1';
    groups.forEach((group) => {
      group.addEventListener('click', () => {
        if (group.getAttribute('aria-expanded') === 'true') return;
        switchGroup(root, group);
      });
    });
  };

  // 2-2: 接近時取得の遅延ロード用オブザーバ（全セクション共有・非対応時はnullで即時読込にフォールバック）
  const lazyObserver =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              obs.unobserve(entry.target);
              loadSection(entry.target);
            });
          },
          { rootMargin: LAZY_ROOT_MARGIN, threshold: 0 }
        )
      : null;

  // 配下の未処理セクションを初期化（多重実行・エディタ再描画に耐え、即時取得せず遅延ロード用オブザーバに登録）
  const init = (ctx) => {
    const scope = ctx && ctx.querySelectorAll ? ctx : document;
    const roots = scope.matches && scope.matches(SELECTOR) ? [scope] : Array.from(scope.querySelectorAll(SELECTOR));
    roots.forEach((root) => {
      if (root.dataset.cZetaCollectionReady) return;
      root.dataset.cZetaCollectionReady = '1';
      normalizeShopifyCards(root);
      setupTabs(root);
      setupGroups(root);
      if (lazyObserver) {
        lazyObserver.observe(root);
      } else {
        loadSection(root); // フォールバック：即時読込
      }
    });
  };

  if (!window.Zeta) {
    console.warn('[c_zeta-collection] window.Zeta が初期化されていません。c_zeta.js の読み込みを確認してください。');
    return;
  }

  init(document);

  // テーマエディタでセクションが追加/再描画されたとき
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target) init(e.target);
  });

  document.addEventListener('shopify:block:select', (e) => {
    const target = e.target && e.target.closest
      ? e.target.closest('[data-c_zeta-collection-group], [data-c_zeta-collection-tab]')
      : null;
    if (target) target.click();
  });
})();
